import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './OperatorInvoices.css'; // Reusing invoice styles
import { formatCurrency } from '../utils/formatters';

const API = '/api/quotations';
const CLIENTS_API = '/api/clients';
const ROUTES_API = '/api/routes';

/* ── Inline SVG Icons ── */
const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    check: ['M20 6 9 17l-5-5'],
    eye: ['M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z', 'M12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
};

export default function OperatorQuotations({ user: propUser, operatorDetails }) {
    const [quotations, setQuotations] = useState([]);
    const [clients, setClients] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);

    const user = propUser || JSON.parse(localStorage.getItem('ops-user'));
    const operatorId = user?.operatorId || (user?.userType === 'operator' ? user._id : 'all');
    const country = operatorDetails?.country;

    const [formData, setFormData] = useState({
        clientId: '',
        quotationNumber: '',
        issueDate: new Date().toISOString().split('T')[0],
        validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        billingStartDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
        billingEndDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
        items: [{ routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }],
        additionalAmount: 0,
        vatPercentage: 0,
        notes: '',
        status: 'Draft'
    });

    useEffect(() => {
        if (operatorId) fetchData();
    }, [operatorId]);

    const fetchData = async () => {
        if (!operatorId) return;
        setLoading(true);
        console.log("Fetching data for operator:", operatorId);

        try {
            const qRes = await axios.get(`${API}/${operatorId}`);
            setQuotations(qRes.data);
        } catch (err) { console.error("Quotations fetch error:", err); }

        try {
            const clientRes = await axios.get(`${CLIENTS_API}/${operatorId}`);
            console.log("Clients fetched:", clientRes.data.length);
            setClients(clientRes.data);
        } catch (err) { console.error("Clients fetch error:", err); }

        try {
            const routeRes = await axios.get(`${ROUTES_API}/${operatorId}`);
            setRoutes(routeRes.data);
        } catch (err) { console.error("Routes fetch error:", err); }

        setLoading(false);
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditItem(item);
            setFormData({
                clientId: item.clientId?._id || item.clientId,
                quotationNumber: item.quotationNumber,
                issueDate: new Date(item.issueDate).toISOString().split('T')[0],
                validUntil: new Date(item.validUntil).toISOString().split('T')[0],
                billingStartDate: item.billingStartDate ? new Date(item.billingStartDate).toISOString().split('T')[0] : '',
                billingEndDate: item.billingEndDate ? new Date(item.billingEndDate).toISOString().split('T')[0] : '',
                items: item.items || [],
                additionalAmount: item.additionalAmount || 0,
                vatPercentage: item.vatPercentage || 0,
                notes: item.notes || '',
                status: item.status || 'Draft'
            });
        } else {
            setEditItem(null);
            setFormData({
                clientId: '',
                quotationNumber: `QTN-${Date.now().toString().slice(-6)}`,
                issueDate: new Date().toISOString().split('T')[0],
                validUntil: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                billingStartDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
                billingEndDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0],
                items: [{ routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }],
                additionalAmount: 0,
                vatPercentage: 0,
                notes: '',
                status: 'Draft'
            });
        }
        setModalOpen(true);
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        const item = { ...newItems[index] };

        if (field === 'routeId') {
            const selectedRoute = routes.find(r => r._id === value);
            item.routeId = value;
            item.routeName = selectedRoute ? selectedRoute.name : '';
        } else {
            item[field] = value;
        }

        const totalTrips = (Number(item.tripsMonthly) || 0) + (Number(item.tripsDaily) || 0);
        item.totalAmount = totalTrips * (Number(item.amountPerTrip) || 0);

        newItems[index] = item;
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const calculateTotals = () => {
        const tripsSubtotal = formData.items.reduce((sum, item) => sum + (Number(item.totalAmount) || 0), 0);
        const subtotal = tripsSubtotal + (Number(formData.additionalAmount) || 0);
        const vatAmount = subtotal * (Number(formData.vatPercentage) / 100);
        const totalAmount = subtotal + vatAmount;
        return { tripsSubtotal, subtotal, vatAmount, totalAmount };
    };

    const { tripsSubtotal, subtotal, vatAmount, totalAmount } = calculateTotals();

    const handleSave = async (e) => {
        e.preventDefault();

        if (!operatorId || operatorId === 'all') {
            alert("Error: Invalid Operator context. Cannot save quotation.");
            return;
        }

        if (!formData.clientId) {
            alert("Please select a client.");
            return;
        }

        const data = { ...formData, operatorId, tripsSubtotal, subtotal, vatAmount, totalAmount };
        try {
            if (editItem) {
                await axios.put(`${API}/${editItem._id}`, data);
            } else {
                await axios.post(API, data);
            }
            setModalOpen(false);
            fetchData();
        } catch (err) {
            console.error("Save error:", err);
            const msg = err.response?.data?.message || err.message;
            alert("Failed to save quotation: " + msg);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this quotation?")) return;
        try {
            await axios.delete(`${API}/${id}`);
            fetchData();
        } catch (err) { }
    };

    const filtered = quotations.filter(q =>
        q.quotationNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.clientId?.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="operator-page">
            <div className="op-breadcrumb">
                <span className="home-icon">🏠</span>
                <span className="sep">›</span>
                <span>Finance</span>
                <span className="sep">›</span>
                <span className="current">Quotations</span>
            </div>

            <div className="op-header">
                <div className="op-header-left">
                    <h1 className="op-title">Price Quotations</h1>
                    <p className="op-subtitle">Manage commercial proposals and quotations for your clients</p>
                </div>
            </div>

            <div className="op-actions-bar">
                <div className="op-search-box">
                    <span className="search-icon"><Icon d={icons.search} size={14} /></span>
                    <input
                        type="text"
                        placeholder="Search By Quotation # or Client"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <button className="op-create-btn" onClick={() => handleOpenModal()}>
                    <Icon d={icons.plus} size={14} />
                    Create Quotation
                </button>
            </div>

            <div className="op-table-container">
                <table className="op-table">
                    <thead>
                        <tr>
                            <th>QUOTATION ID</th>
                            <th>CLIENT</th>
                            <th>ISSUE DATE</th>
                            <th>VALID UNTIL</th>
                            <th>TOTAL</th>
                            <th>STATUS</th>
                            <th>ACTIONS</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
                        ) : filtered.length === 0 ? (
                            <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>No records found</td></tr>
                        ) : filtered.map((q) => (
                            <tr key={q._id}>
                                <td className="inv-id">{q.quotationNumber}</td>
                                <td>{q.clientId?.companyName || 'Unknown'}</td>
                                <td>{new Date(q.issueDate).toLocaleDateString()}</td>
                                <td>{new Date(q.validUntil).toLocaleDateString()}</td>
                                <td className="inv-amount">{formatCurrency(q.totalAmount, country)}</td>
                                <td><span className={`status-badge ${q.status.toLowerCase()}`}>{q.status}</span></td>
                                <td className="inv-actions">
                                    <button title="Edit" onClick={() => handleOpenModal(q)}><Icon d={icons.edit} size={14} /></button>
                                    <button title="Delete" className="delete-btn" onClick={() => handleDelete(q._id)}><Icon d={icons.trash} size={14} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {modalOpen && (
                <div className="invoice-modal-overlay" onClick={() => setModalOpen(false)}>
                    <div className="invoice-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="invoice-modal-header">
                            <h2>{editItem ? 'Edit Quotation' : 'New Quotation'}</h2>
                            <button className="close-btn" onClick={() => setModalOpen(false)}><Icon d={icons.x} size={20} /></button>
                        </div>
                        <form onSubmit={handleSave}>
                            <div className="invoice-modal-body">
                                <div className="invoice-form-grid">
                                    <div className="form-group">
                                        <label>Client</label>
                                        <select required value={formData.clientId} onChange={e => setFormData({ ...formData, clientId: e.target.value })}>
                                            <option value="">Select Client</option>
                                            {clients.map(c => <option key={c._id} value={c._id}>{c.companyName}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Quotation #</label>
                                        <input type="text" required value={formData.quotationNumber} onChange={e => setFormData({ ...formData, quotationNumber: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Issue Date</label>
                                        <input type="date" required value={formData.issueDate} onChange={e => setFormData({ ...formData, issueDate: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Valid Until</label>
                                        <input type="date" required value={formData.validUntil} onChange={e => setFormData({ ...formData, validUntil: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Billing Period (Start)</label>
                                        <input type="date" value={formData.billingStartDate} onChange={e => setFormData({ ...formData, billingStartDate: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Billing Period (End)</label>
                                        <input type="date" value={formData.billingEndDate} onChange={e => setFormData({ ...formData, billingEndDate: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Status</label>
                                        <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}>
                                            <option value="Draft">Draft</option>
                                            <option value="Sent">Sent</option>
                                            <option value="Accepted">Accepted</option>
                                            <option value="Declined">Declined</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="invoice-items-section">
                                    <div className="items-header">
                                        <h3>Quotation Items</h3>
                                        <button type="button" className="add-item-btn" onClick={() => setFormData(f => ({ ...f, items: [...f.items, { routeId: '', routeName: '', tripsMonthly: 0, tripsDaily: 0, amountPerTrip: 0, totalAmount: 0 }] }))}>
                                            <Icon d={icons.plus} size={12} /> Add Item
                                        </button>
                                    </div>
                                    <table className="items-table">
                                        <thead>
                                            <tr><th>Route</th><th>Monthly</th><th>Daily</th><th>Price</th><th>Total</th><th></th></tr>
                                        </thead>
                                        <tbody>
                                            {formData.items.map((item, idx) => (
                                                <tr key={idx}>
                                                    <td>
                                                        <select required value={item.routeId} onChange={e => handleItemChange(idx, 'routeId', e.target.value)}>
                                                            <option value="">Select Route</option>
                                                            {routes.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
                                                        </select>
                                                    </td>
                                                    <td><input type="number" value={item.tripsMonthly} onChange={e => handleItemChange(idx, 'tripsMonthly', e.target.value)} /></td>
                                                    <td><input type="number" value={item.tripsDaily} onChange={e => handleItemChange(idx, 'tripsDaily', e.target.value)} /></td>
                                                    <td><input type="number" step="0.001" value={item.amountPerTrip} onChange={e => handleItemChange(idx, 'amountPerTrip', e.target.value)} /></td>
                                                    <td>{formatCurrency(item.totalAmount, country)}</td>
                                                    <td>
                                                        {formData.items.length > 1 && <button type="button" onClick={() => {
                                                            const items = [...formData.items]; items.splice(idx, 1); setFormData(f => ({ ...f, items }));
                                                        }}>x</button>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="invoice-totals-section">
                                    <div className="totals-form-grid">
                                        <div className="form-group">
                                            <label>VAT (%)</label>
                                            <select value={formData.vatPercentage} onChange={e => setFormData({ ...formData, vatPercentage: e.target.value })}>
                                                <option value="0">0%</option>
                                                <option value="5">5%</option>
                                                <option value="15">15%</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="totals-display-box">
                                        <div className="total-row final-total">
                                            <span>Total Amount:</span>
                                            <span className="grand-total">{formatCurrency(totalAmount, country)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="invoice-modal-footer">
                                <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
                                <button type="submit" className="btn-save">{editItem ? 'Update Quotation' : 'Save Quotation'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
